// src/pages/VerifyEmailPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Loader2, CheckCircle2, Eye, EyeOff, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import dvsLogo from '../assets/branding/dva_logo.png';

const SESSIONS_API_URL = import.meta.env.VITE_SESSIONS_API_URL || 'https://dva-admin-backend-dev-813595575009.us-central1.run.app';
const PUBLIC_API_KEY = import.meta.env.VITE_PUBLIC_API_KEY;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, validateToken, logout } = useAuth();

  // Extract token from URL
  const tokenParam = searchParams.get('token');

  // Ref to prevent duplicate API calls (React StrictMode issue)
  const hasValidated = useRef(false);

  // Form state
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const [validationState, setValidationState] = useState('checking');
  const [passwordAlreadySet, setPasswordAlreadySet] = useState(false);

  // Validate token with API on component mount
  useEffect(() => {
    const validateToken = async () => {
      // Prevent duplicate calls (React StrictMode runs effects twice in dev)
      if (hasValidated.current) {
        console.log('⏭️ Skipping duplicate token validation');
        return;
      }

      if (!tokenParam) {
        setError('No verification token provided in the URL');
        setValidationState('invalid');
        setLoading(false);
        return;
      }

      // Mark as validating to prevent duplicate calls
      hasValidated.current = true;

      try {
        setLoading(true);
        setVerifying(true);
        setToken(tokenParam);

        console.log('🔍 Validating token:', tokenParam);
        console.log('📡 API URL:', `${SESSIONS_API_URL}/auth/pending-account/${tokenParam}`);

        // Validate token with backend API
        const response = await fetch(`${SESSIONS_API_URL}/auth/pending-account/${tokenParam}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': PUBLIC_API_KEY,
          },
        });

        console.log('📊 Response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ API error response:', errorData);

          // Check if password is already set
          if (response.status === 409 || errorData.error?.includes('already set')) {
            console.log('⚠️ Password already set for this account');
            setPasswordAlreadySet(true);
            setValidationState('password-exists');
            return;
          }

          throw new Error(errorData.message || errorData.error || 'Invalid or expired verification token');
        }

        const responseData = await response.json();
        console.log('✅ Token validated successfully:', responseData);

        // Extract email and user_name from response data
        const { email: userEmail, user_name: displayName } = responseData.data || {};

        if (!userEmail) {
          throw new Error('Invalid response: missing email');
        }

        setEmail(userEmail);
        setUserName(displayName || '');
        setValidationState('valid');
        setError('');
      } catch (err) {
        console.error('❌ Token validation error:', err);
        console.error('Error message:', err.message);

        setError(err.message || 'Failed to validate verification token. Please try again or contact support.');
        setValidationState('invalid');
      } finally {
        setLoading(false);
        setVerifying(false);
      }
    };

    validateToken();

    // Cleanup: Reset hasValidated if the token changes
    return () => {
      hasValidated.current = false;
    };
  }, [tokenParam]);

  // Password validation
  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);

      console.log('🔐 Setting password for token:', token);
      console.log('📡 API URL:', `${SESSIONS_API_URL}/auth/set-password`);

      // Call the backend API to set password with token
      const response = await fetch(`${SESSIONS_API_URL}/auth/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': PUBLIC_API_KEY,
        },
        body: JSON.stringify({ token, password }),
      });

      console.log('📊 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API error response:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to set password');
      }

      const data = await response.json();
      console.log('✅ Password set successfully:', data);

      // Show logging in state
      setSubmitting(false);
      setLoggingIn(true);

      // Wait for backend to complete session setup before auto-login (6 seconds)
      console.log('⏳ Waiting for backend session setup...');
      await new Promise(resolve => setTimeout(resolve, 6000));

      // Auto-login the user
      try {
        // Step 1: Firebase Authentication
        console.log('🔐 Logging in user...');
        const user = await login(email, password);
        console.log('✅ Firebase auth successful:', user.uid);

        // Step 2: Get JWT token
        const token = await user.getIdToken();
        console.log('✅ JWT token obtained');

        // Step 3: Validate token with backend (creates session document)
        try {
          await validateToken(token, user.uid);
          console.log('✅ Session validated and subscribed');

          // Step 4: Navigate to home on success
          console.log('🏠 Redirecting to /home');
          navigate('/home');
        } catch (sessionError) {
          console.error('❌ Session validation failed:', sessionError);

          // Block access if session validation fails
          setLoggingIn(false);
          setError(sessionError.message || 'Session validation failed. Please try logging in manually.');

          // Sign out user since session validation failed
          await logout();
        }
      } catch (loginErr) {
        console.error('❌ Auto-login failed:', loginErr);
        // Password was set successfully but login failed
        // Redirect to login page with a message
        setLoggingIn(false);
        setError('Password set successfully! Please log in with your new password.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Set password error:', err);
      setError(err.message || 'Failed to set password. Please try again or contact support.');
    } finally {
      setSubmitting(false);
      setLoggingIn(false);
    }
  };


  // Loading screen
  if (loading || verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#18365E] animate-spin mx-auto mb-4" />
              <h2
                className="text-xl mb-2"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Verifying Token...
              </h2>
              <p className="text-[#717182]" style={{ fontWeight: 500 }}>
                Please wait while we verify your verification token
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Logging in screen
  if (loggingIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#18365E] animate-spin mx-auto mb-4" />
              <h2
                className="text-xl mb-2"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Setting Up Your Account...
              </h2>
              <p className="text-[#717182]" style={{ fontWeight: 500 }}>
                Please wait while we complete your account setup and log you in
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Password already set screen
  if (validationState === 'password-exists') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                <CheckCircle2 className="w-8 h-8 text-blue-600" />
              </div>

              <h2
                className="text-2xl mb-3"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Password Already Set
              </h2>

              <p className="text-[#717182] mb-6" style={{ fontWeight: 500 }}>
                You already have a password set for <strong>{email}</strong>. Please use the login page to access your account.
              </p>

              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block w-full px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all text-center"
                  style={{ fontWeight: 600 }}
                >
                  Go to Login
                </Link>

                <Link
                  to="/forgot-password"
                  className="block w-full px-6 py-3 border-2 border-[#18365E] text-[#18365E] rounded-lg hover:bg-[#18365E]/5 transition-all text-center"
                  style={{ fontWeight: 600 }}
                >
                  Forgot Password?
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token screen
  if (validationState === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>

              <h2
                className="text-2xl mb-3"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Token Verification Failed
              </h2>

              <p className="text-[#717182] mb-6" style={{ fontWeight: 500 }}>
                {error}
              </p>

              <div className="space-y-3">
                <Link
                  to="/signup"
                  className="block w-full px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all text-center"
                  style={{ fontWeight: 600 }}
                >
                  Sign Up
                </Link>

                <Link
                  to="/login"
                  className="block w-full px-6 py-3 border-2 border-[#18365E] text-[#18365E] rounded-lg hover:bg-[#18365E]/5 transition-all text-center"
                  style={{ fontWeight: 600 }}
                >
                  Go to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Password setting form
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-6">
            <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
          </div>
          {userName && (
            <p className="text-lg text-[#18365E] mb-2" style={{ fontWeight: 600 }}>
              Welcome, {userName}!
            </p>
          )}
          <h1
            className="text-3xl mb-2"
            style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
          >
            Set Your Password
          </h1>
          <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
            Your email has been verified. Please set a password for your account.
          </p>
        </div>

        {/* Password Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700" style={{ fontWeight: 500 }}>
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email (read-only) */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm mb-2 text-[#18365E]"
                style={{ fontWeight: 600 }}
              >
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  readOnly
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-[#717182] cursor-not-allowed"
                  style={{ fontWeight: 500 }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm mb-2 text-[#18365E]"
                style={{ fontWeight: 600 }}
              >
                Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                  style={{ fontWeight: 500 }}
                  disabled={submitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717182] hover:text-[#18365E] transition-colors"
                  disabled={submitting}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                At least 8 characters with uppercase, lowercase, and numbers
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm mb-2 text-[#18365E]"
                style={{ fontWeight: 600 }}
              >
                Confirm Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                  style={{ fontWeight: 500 }}
                  disabled={submitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717182] hover:text-[#18365E] transition-colors"
                  disabled={submitting}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontWeight: 600 }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Setting up your account...
                </>
              ) : (
                'Set Password & Continue'
              )}
            </button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
              Already have a password?{' '}
              <Link
                to="/login"
                className="text-[#FF6E4C] hover:underline"
                style={{ fontWeight: 600 }}
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
